import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import api from "../../lib/api";

/**
 * MoMDeltaPill — compact "vs 30d anteriores" indicator embedded inside
 * each Score card (Conversion, Attribution, DPO). Fetches once per page
 * load from `/api/scorecard/kpi-comparison` and shares the response
 * across every mounted pill via a module-level cache (avoids N parallel
 * requests when three cards mount simultaneously).
 *
 * Props:
 *   metric: "conversion" | "attribution" | "dpo"
 *   inverse?: boolean — reverse the good/bad color mapping (unused today
 *             but reserved for cost-style KPIs where lower is better).
 */

// Module-level in-memory cache (shared by every mounted pill).
let _cache = null;
let _pending = null;
const _CACHE_TTL_MS = 5 * 60_000;

async function fetchComparison() {
  const now = Date.now();
  if (_cache && (now - _cache.at) < _CACHE_TTL_MS) return _cache.data;
  if (_pending) return _pending;
  _pending = (async () => {
    try {
      const r = await api.get("/scorecard/kpi-comparison");
      _cache = { at: Date.now(), data: r.data };
      return r.data;
    } finally { _pending = null; }
  })();
  return _pending;
}

export default function MoMDeltaPill({ metric, inverse = false }) {
  const [d, setD] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let m = true;
    fetchComparison().then((data) => {
      if (!m) return;
      setD(data?.[metric] || null);
      setReady(true);
    }).catch(() => { if (m) setReady(true); });
    return () => { m = false; };
  }, [metric]);

  if (!ready) return null;
  if (!d || !d.available || d.delta_pct == null) {
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-bold text-gray-400 bg-gray-50"
        title="Sin baseline histórico aún — se acumulará con el sync nocturno."
        data-testid={`mom-pill-${metric}-na`}
      >
        <Minus size={10} strokeWidth={2.5} />
        vs 30d N/D
      </span>
    );
  }

  const up = d.trend === "up";
  const down = d.trend === "down";
  const good = inverse ? down : up;
  const bad  = inverse ? up   : down;
  const color = good ? "#16a34a" : bad ? "#dc2626" : "#6b7280";
  const bg    = good ? "#16a34a14" : bad ? "#dc262614" : "#6b728014";
  const Icon  = up ? TrendingUp : down ? TrendingDown : Minus;
  const sign  = d.delta_pct > 0 ? "+" : "";

  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-bold tabular-nums"
      style={{ color, backgroundColor: bg }}
      title={`Actual ${d.current} · anterior ${d.previous} (30d)`}
      data-testid={`mom-pill-${metric}`}
    >
      <Icon size={10} strokeWidth={2.5} />
      {sign}{d.delta_pct.toFixed(1)}% vs 30d
    </span>
  );
}
